import React from 'react';
import { Card, Row, Col, Statistic, Progress, Tag } from 'antd';
import { ProcessSummary } from '../../services/api/dashboardService';
import './ProcessTypeSummaryCards.css';

interface ProcessTypeSummaryCardsProps {
  processSummary: ProcessSummary[];
  onProcessTypeSelect: (processType: string) => void;
  selectedProcessType: string | null;
}

export const ProcessTypeSummaryCards: React.FC<ProcessTypeSummaryCardsProps> = ({
  processSummary,
  onProcessTypeSelect,
  selectedProcessType,
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'constrained':
        return 'red';
      case 'active':
        return 'green';
      case 'underutilized':
        return 'orange';
      default:
        return 'default';
    }
  };

  const getAverageUtilization = (processes: ProcessSummary['processes']) => {
    if (processes.length === 0) return 0;
    const totalUtilization = processes.reduce(
      (sum, process) => sum + process.capacity_utilization,
      0
    );
    return totalUtilization / processes.length;
  };

  const getBottleneckCount = (processes: ProcessSummary['processes']) => {
    return processes.filter((process) => process.status === 'constrained').length;
  };

  return (
    <div className="process-type-summary-cards">
      <h2>Process Type Summary</h2>
      <Row gutter={[16, 16]}>
        {processSummary.map((summary) => {
          const avgUtilization = getAverageUtilization(summary.processes);
          const bottleneckCount = getBottleneckCount(summary.processes);
          const isSelected = selectedProcessType === summary.process_type;

          return (
            <Col xs={24} sm={12} md={8} lg={6} key={summary.process_type}>
              <Card
                className={`process-type-card ${isSelected ? 'selected' : ''}`}
                hoverable
                onClick={() => onProcessTypeSelect(summary.process_type)}
              >
                <div className="card-header">
                  <h3>{summary.process_type}</h3>
                  <Tag color={bottleneckCount > 0 ? 'red' : 'green'}>
                    {bottleneckCount} {bottleneckCount === 1 ? 'Bottleneck' : 'Bottlenecks'}
                  </Tag>
                </div>

                <div className="card-stats">
                  <Statistic title="Processes" value={summary.process_count} suffix="processes" />

                  <Statistic title="Total Items" value={summary.total_items} suffix="items" />
                </div>

                <div className="utilization-section">
                  <div className="utilization-label">
                    <span>Average Utilization</span>
                    <span className="utilization-value">{avgUtilization.toFixed(1)}%</span>
                  </div>
                  <Progress
                    percent={avgUtilization}
                    size="small"
                    status={
                      avgUtilization >= 90
                        ? 'exception'
                        : avgUtilization >= 70
                          ? 'active'
                          : 'normal'
                    }
                    strokeColor={{
                      '0%': '#108ee9',
                      '100%':
                        avgUtilization >= 90
                          ? '#ff4d4f'
                          : avgUtilization >= 70
                            ? '#52c41a'
                            : '#faad14',
                    }}
                  />
                </div>

                <div className="process-status-summary">
                  {summary.processes.slice(0, 3).map((process) => (
                    <div key={process.process_id} className="process-status-item">
                      <span className="process-name">{process.process_name}</span>
                      <Tag color={getStatusColor(process.status)}>{process.status}</Tag>
                    </div>
                  ))}
                  {summary.processes.length > 3 && (
                    <div className="more-processes">
                      +{summary.processes.length - 3} more processes
                    </div>
                  )}
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>

      {processSummary.length === 0 && (
        <div className="no-data">
          <p>No process data available. Please check your filters or create some processes.</p>
        </div>
      )}
    </div>
  );
};
